import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingService } from '../../services/bookingService';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import AppIcon from '../../components/AppIcon';
import { cn } from '../../utils/cn';

const DashboardHome = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    upcomingBookings: 0,
    pendingRequests: 0,
    completedBookings: 0,
  });
  const [accountStatus, setAccountStatus] = useState('active');
  const [photographerData, setPhotographerData] = useState(null);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [availableForBooking, setAvailableForBooking] = useState(false);
  const [hasServiceCenter, setHasServiceCenter] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    // Initial load with loading state
    loadDashboardData();

    // Refresh dashboard silently in background every 30 seconds (fallback)
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    // Real-time subscription: يتحدث فوراً عند تغيّر حالة أي حجز للمصور
    const channel = supabase
      .channel('dashboard-bookings-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `assigned_photographer_id=eq.${userProfile?.id}`,
        },
        () => { loadDashboardData(); }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [userProfile?.id]);

  const loadDashboardData = async (showLoading = false) => {
    try {
      // Only show loading spinner on initial load
      if (isInitialLoad.current) {
        setLoading(true);
      }

      // Load photographer profile (only on initial load)
      if (isInitialLoad.current) {
        const photographer = await photographerService.getPhotographer(userProfile?.id);
        setPhotographerData(photographer);

        // Availability & service center
        const centerLat = photographer?.serviceCenterLat ?? photographer?.service_center_lat ?? null;
        const centerLng = photographer?.serviceCenterLng ?? photographer?.service_center_lng ?? null;
        const hasCenter = !!(centerLat && centerLng);
        setHasServiceCenter(hasCenter);
        const isAvailable = photographer?.availableForBooking ?? photographer?.available_for_booking ?? false;
        setAvailableForBooking(hasCenter ? isAvailable : false);

        // Determine account status from database
        const s = photographer?.status;
        const now = new Date();
        const suspendedUntilRaw = photographer?.withdrawalSuspendedUntil ?? photographer?.withdrawal_suspended_until;
        const visibilityPenaltyRaw = photographer?.visibilityPenaltyUntil ?? photographer?.visibility_penalty_until;
        const isSuspendedByWithdrawal =
          suspendedUntilRaw && new Date(suspendedUntilRaw) > now;
        const isHiddenByWithdrawal =
          !isSuspendedByWithdrawal &&
          visibilityPenaltyRaw && new Date(visibilityPenaltyRaw) > now;

        if (isSuspendedByWithdrawal) {
          setAccountStatus('withdrawal_suspended');
        } else if (isHiddenByWithdrawal) {
          setAccountStatus('withdrawal_hidden');
        } else if (s === 'approved' || s === 'active') {
          setAccountStatus('active');
        } else if (s === 'suspended' || s === 'inactive') {
          setAccountStatus('suspended');
        } else if (s === 'pending' || s === 'under_review') {
          setAccountStatus('under_review');
        } else {
          setAccountStatus('under_review');
        }
      }

      // Load bookings (always refresh to get new bookings)
      const bookings = await bookingService.getMyBookings('photographer');

      // Calculate stats
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const upcoming = bookings.filter((b) => {
        if (b.status === 'session_started' || b.status === 'awaiting_delivery') return true;
        if (b.status === 'paid_confirmed') {
          const d = new Date(b.scheduledAt || b.bookingDate || b.booking_date);
          return !isNaN(d.getTime()) && d >= startOfToday;
        }
        return false;
      });
      const pending = bookings.filter((b) => b.status === 'pending_photographer_confirmation');
      const completed = bookings.filter((b) => b.status === 'completed');

      setStats({
        upcomingBookings: upcoming.length,
        pendingRequests: pending.length,
        completedBookings: completed.length,
      });
      
      // Set pending bookings for display
      setPendingBookings(pending);
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Error handling - ensure loading is set to false even on error
    } finally {
      // Always hide loading spinner after initial load completes
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        setLoading(false);
      }
    }
  };

  const handleToggleAvailability = async () => {
    if (savingAvailability) return;
    if (!hasServiceCenter && !availableForBooking) return;
    const newValue = !availableForBooking;
    setAvailableForBooking(newValue);
    setSavingAvailability(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('not authenticated');
      const { data: profile } = await supabase
        .from('photographer_profiles')
        .select('service_center_lat, service_center_lng')
        .eq('user_id', user.id)
        .single();
      const freshHasCenter = !!(profile?.service_center_lat && profile?.service_center_lng);
      setHasServiceCenter(freshHasCenter);
      if (newValue && !freshHasCenter) {
        setAvailableForBooking(false);
        return;
      }
      await supabase
        .from('photographer_profiles')
        .update({ available_for_booking: newValue, updated_at: new Date().toISOString() })
        .eq('user_id', user.id);
    } catch {
      setAvailableForBooking(!newValue);
    } finally {
      setSavingAvailability(false);
    }
  };

  const getExperienceLevelBadge = (level) => {
    const DB_TO_UI = { beginner: 'certified', intermediate: 'distinguished', advanced: 'distinguished', expert: 'elite' };
    const uiLevel = DB_TO_UI[level] || level;
    const config = {
      certified: { label: 'معتمد', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: 'Award' },
      distinguished: { label: 'مميز', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: 'Star' },
      elite: { label: 'نخبة', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: 'Crown' },
    };
    const c = config[uiLevel];
    if (!c) return null;
    return (
      <span className={cn('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border', c.color)}>
        <AppIcon name={c.icon} size={14} />
        {c.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        label: 'نشط',
        color: 'bg-green-100 text-green-700 border-green-300',
        icon: 'CheckCircle',
      },
      suspended: {
        label: 'موقوف',
        color: 'bg-red-100 text-red-700 border-red-300',
        icon: 'Ban',
      },
      under_review: {
        label: 'تحت المراجعة',
        color: 'bg-amber-100 text-amber-700 border-amber-300',
        icon: 'Clock',
      },
      withdrawal_suspended: {
        label: 'موقوف مؤقتاً',
        color: 'bg-red-100 text-red-700 border-red-300',
        icon: 'Ban',
      },
      withdrawal_hidden: {
        label: 'مخفي مؤقتاً',
        color: 'bg-orange-100 text-orange-700 border-orange-300',
        icon: 'EyeOff',
      },
    };

    const config = statusConfig[status] || statusConfig.under_review;

    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border',
          config.color
        )}
      >
        <AppIcon name={config.icon} size={16} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const photographerName = photographerData?.user?.fullName || userProfile?.fullName || 'مصور';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 mb-2 break-words" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              حياك، {photographerName}
            </h1>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {getStatusBadge(accountStatus)}
              {photographerData?.experience_level && getExperienceLevelBadge(photographerData.experience_level)}
            </div>
          </div>

          {/* زر التوفر المصغّر — متزامن مع صفحة التوفر */}
          <div className="flex-shrink-0">
            <button
              onClick={handleToggleAvailability}
              disabled={(!hasServiceCenter && !availableForBooking) || savingAvailability}
              title={!hasServiceCenter ? 'يجب تحديد مركز منطقة الخدمة أولاً' : availableForBooking ? 'متاح للحجز' : 'غير متاح للحجز'}
              className={cn(
                'relative inline-flex items-center justify-between gap-2 px-3 py-2 rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 min-w-[120px] overflow-hidden text-sm',
                availableForBooking
                  ? 'bg-gradient-to-r from-[#34c759] to-[#30d158] text-white shadow-md shadow-[#34c759]/30 focus:ring-[#34c759]'
                  : 'bg-gradient-to-r from-slate-300 to-slate-400 text-slate-700 shadow focus:ring-slate-400',
                (!hasServiceCenter && !availableForBooking) && 'opacity-50 cursor-not-allowed',
                savingAvailability && 'opacity-80 cursor-wait',
                hasServiceCenter && !savingAvailability && 'cursor-pointer hover:shadow-lg active:scale-[0.98]'
              )}
              role="switch"
              aria-checked={availableForBooking}
            >
              {/* دائرة التبديل */}
              <span className={cn(
                'absolute right-1 inline-flex items-center justify-center h-6 w-6 rounded-full bg-white shadow transition-all duration-300 ease-in-out z-10',
                availableForBooking ? 'translate-x-[calc(100%-1.75rem)]' : 'translate-x-0'
              )}>
                {savingAvailability
                  ? <span className="animate-spin inline-block h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                  : availableForBooking
                    ? <AppIcon name="Check" size={12} className="text-[#34c759]" />
                    : <AppIcon name="X" size={12} className="text-slate-400" />
                }
              </span>
              {/* النص */}
              <span className={cn(
                'font-semibold whitespace-nowrap transition-all duration-300 z-0',
                availableForBooking ? 'mr-7 ml-auto' : 'ml-7 mr-auto'
              )}>
                {availableForBooking ? 'متاح' : 'غير متاح'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Pending Booking Requests — directly under the photographer name card */}
      {stats.pendingRequests > 0 && (
        <div className="relative overflow-hidden bg-white rounded-xl sm:rounded-2xl shadow-sm border border-blue-100 p-5 sm:p-6 flex items-center gap-4 sm:gap-6">
          {/* Accent bar */}
          <div className="absolute top-0 right-0 h-full w-1 bg-primary rounded-r-xl sm:rounded-r-2xl" />

          {/* Icon */}
          <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-blue-50 flex items-center justify-center">
            <AppIcon name="BellRing" size={24} color="#2563eb" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm text-slate-500 mb-0.5" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              طلب حجز جديد
            </p>
            <p className="text-base sm:text-lg font-bold text-slate-900 leading-tight" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              لديك{' '}
              <span className="text-primary">{stats.pendingRequests}</span>{' '}
              {stats.pendingRequests === 1 ? 'طلب' : 'طلبات'} بانتظار تأكيدك
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/photographer-dashboard/bookings?tab=pending')}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-primary hover:opacity-90 active:scale-95 text-white rounded-xl text-sm font-semibold transition-all shadow-sm"
            style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
          >
            <span>عرض الطلب</span>
            <AppIcon name="ChevronLeft" size={16} color="white" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-blue-50 rounded-lg sm:rounded-xl">
              <AppIcon name="Calendar" size={20} className="sm:w-6 sm:h-6" color="#2563eb" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            الحجوزات القادمة
          </h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.upcomingBookings}</p>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-slate-100 rounded-lg sm:rounded-xl">
              <AppIcon name="Clock" size={20} className="sm:w-6 sm:h-6" color="#64748b" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            طلبات معلقة
          </h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.pendingRequests}</p>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-emerald-50 rounded-lg sm:rounded-xl">
              <AppIcon name="CheckCircle" size={20} className="sm:w-6 sm:h-6" color="#10b981" />
            </div>
          </div>
          <h3 className="text-xs sm:text-sm text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            الحجوزات المكتملة
          </h3>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900">{stats.completedBookings}</p>
        </div>
      </div>

      {/* Primary Actions */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 sm:mb-4" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
          الإجراءات السريعة
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/photographer-dashboard/bookings')}
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors text-right"
          >
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <AppIcon name="Calendar" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                الحجوزات
              </h3>
              <p className="text-xs sm:text-sm text-slate-600">إدارة جميع حجوزاتك</p>
            </div>
            <AppIcon name="ChevronLeft" size={18} className="sm:w-5 sm:h-5 flex-shrink-0" color="#64748b" />
          </button>

          <button
            onClick={() => navigate('/photographer-dashboard/bookings?tab=delivery')}
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors text-right"
          >
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <AppIcon name="Package" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                التسليم
              </h3>
              <p className="text-xs sm:text-sm text-slate-600">تسليم الصور والفيديوهات للعملاء</p>
            </div>
            <AppIcon name="ChevronLeft" size={18} className="sm:w-5 sm:h-5 flex-shrink-0" color="#64748b" />
          </button>

          <button
            onClick={() => navigate('/photographer-dashboard/payments')}
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors text-right"
          >
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <AppIcon name="Wallet" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                المدفوعات
              </h3>
              <p className="text-xs sm:text-sm text-slate-600">الأرباح والمعلومات البنكية</p>
            </div>
            <AppIcon name="ChevronLeft" size={18} className="sm:w-5 sm:h-5 flex-shrink-0" color="#64748b" />
          </button>

          <button
            onClick={() => navigate('/photographer-dashboard/availability')}
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors text-right"
          >
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <AppIcon name="Clock" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                تحديث التوفر
              </h3>
              <p className="text-xs sm:text-sm text-slate-600">تحديد أوقات توفرك</p>
            </div>
            <AppIcon name="ChevronLeft" size={18} className="sm:w-5 sm:h-5 flex-shrink-0" color="#64748b" />
          </button>

          <button
            onClick={() => navigate('/photographer-dashboard/profile')}
            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-lg sm:rounded-xl border border-slate-200 hover:border-primary hover:bg-primary/5 transition-colors text-right sm:col-span-2 lg:col-span-1"
          >
            <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg flex-shrink-0">
              <AppIcon name="User" size={18} className="sm:w-5 sm:h-5" color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                تعديل الملف الشخصي
              </h3>
              <p className="text-xs sm:text-sm text-slate-600">تحديث معلوماتك</p>
            </div>
            <AppIcon name="ChevronLeft" size={18} className="sm:w-5 sm:h-5 flex-shrink-0" color="#64748b" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
